import { SessionDataProvider } from "../sessionDataProvider/index.js";
import type { WindowObjectApi, ConsoleNavModel } from "../shared/types.js";

declare const window: WindowObjectApi;

const ULTIMATE_FALLBACK_PAGE_TITLE = "AWS Management Console";

const generateSubConsoleLabel = (
  consoleNavModel: Partial<ConsoleNavModel> | undefined,
  targetWindow?: WindowObjectApi["top"] | WindowObjectApi
): string | undefined => {
  const windowLocationPathname =
    targetWindow?.location.pathname.replace(/[^a-zA-Z0-9]/g, "") ||
    window.top.location.pathname.replace(/[^a-zA-Z0-9]/g, "");
  const windowLocationHash =
    targetWindow?.location.hash.split("?")?.[0]?.replace(/[^a-zA-Z0-9]/g, "") ||
    window.top.location.hash.split("?")?.[0]?.replace(/[^a-zA-Z0-9]/g, "");
  const subConsoleUrl = `${windowLocationPathname}${windowLocationHash}`;
  const subConsoleFeature = consoleNavModel?.currentService?.features?.find(
    (featureObj) => featureObj?.url?.replace(/[^a-zA-Z0-9]/g, "") === subConsoleUrl
  );
  return subConsoleFeature?.metadata?.title;
};

const getPageTitle = (
  serviceLabel: string,
  regionId: string,
  subConsoleLabel: string,
  isGlobalService: boolean
): string | undefined => {
  let pageTitle: string | undefined;
  if (serviceLabel) {
    if (subConsoleLabel) {
      if (!isGlobalService) {
        pageTitle = `${subConsoleLabel} | ${serviceLabel} | ${regionId}`;
      } else {
        pageTitle = `${subConsoleLabel} | ${serviceLabel} | Global`;
      }
    } else {
      if (!isGlobalService) {
        pageTitle = `${serviceLabel} | ${regionId}`;
      } else {
        pageTitle = `${serviceLabel} | Global`;
      }
    }
  }
  return pageTitle;
};

const updateTitle = (pageTitle: string | undefined, targetWindow?: WindowObjectApi["top"] | WindowObjectApi) => {
  if (pageTitle) {
    if (targetWindow) {
      targetWindow.document.title = pageTitle;
    } else {
      window.top.document.title = pageTitle;
    }
  }
};

const isTbDomain = (targetWindow?: WindowObjectApi["top"] | WindowObjectApi) => {
  return targetWindow ? targetWindow.location.href.includes("aws-dev") : window.top.location.href.includes("aws-dev");
};

const isMezzUltimateFb = (targetWindow?: WindowObjectApi["top"] | WindowObjectApi) => {
  return targetWindow ? !targetWindow.ConsoleNavService?.Model : !window.top.ConsoleNavService?.Model;
};

const logConsoleWarning = (warning: string, targetWindow?: WindowObjectApi["top"] | WindowObjectApi) => {
  if (!isTbDomain(targetWindow)) {
    console.warn(warning);
  }
};

export const updatePageTitle = async (subConsoleLabel?: string, fromNav?: boolean, windowObj?: WindowObjectApi) => {
  // Use the provided window object if available, otherwise use the global window
  const targetWindow = windowObj ? windowObj : window.top;

  if (isMezzUltimateFb(targetWindow)) {
    updateTitle(ULTIMATE_FALLBACK_PAGE_TITLE, targetWindow);
    return;
  }
  if (!targetWindow?.ConsoleNavService) {
    logConsoleWarning("window.ConsoleNavService doesn't exist in the calling environment", targetWindow);
    return;
  }
  const consoleNavService = targetWindow.ConsoleNavService;
  if (!consoleNavService.Model) {
    logConsoleWarning("window.ConsoleNavService.Model doesn't exist in the calling environment", targetWindow);
    return;
  }
  const consoleNavModel = consoleNavService.Model;
  const currentService = consoleNavModel.currentService;
  if (!currentService) {
    logConsoleWarning("window.ConsoleNavService.Model.currentService is empty", targetWindow);
    return;
  }
  const serviceLabel = currentService.label || "";
  const sessionDataProvider = SessionDataProvider.getInstance();
  let infrastructureRegionId: string | undefined = "";
  try {
    infrastructureRegionId = await sessionDataProvider.getInfrastructureRegion();
  } catch (error) {
    logConsoleWarning("Unable to retrieve infrastructureRegion from SessionDataProvider", targetWindow);
  }
  const currentRegion = consoleNavModel.currentRegion;
  const isGlobalService = currentService.global || false;
  if (!infrastructureRegionId && !currentRegion && !isGlobalService) {
    logConsoleWarning(
      "Unable to retrieve infrastructureRegion from SessionDataProvider and window.ConsoleNavService.Model.currentRegion is empty",
      targetWindow
    );
    return;
  }
  const regionId = (!isGlobalService ? infrastructureRegionId || currentRegion?.id : "") || "";
  if (!regionId && !isGlobalService) {
    logConsoleWarning(
      "Unable to retrieve infrastructureRegion from SessionDataProvider and window.ConsoleNavService.Model.currentRegion.id is empty",
      targetWindow
    );
    return;
  }
  if (!subConsoleLabel) {
    subConsoleLabel = generateSubConsoleLabel(consoleNavModel, targetWindow);
  }
  subConsoleLabel = subConsoleLabel || "";
  const pageTitle = getPageTitle(serviceLabel, regionId, subConsoleLabel, isGlobalService);
  const isPageTitleSetByService = consoleNavService.__IS_PAGE_TITLE_SET_BY_SERVICE__;
  if (fromNav) {
    if (!isPageTitleSetByService) {
      updateTitle(pageTitle, targetWindow);
    } else {
      consoleNavService.__IS_PAGE_TITLE_SET_BY_SERVICE__ = false;
    }
  } else {
    updateTitle(pageTitle, targetWindow);
    consoleNavService.__IS_PAGE_TITLE_SET_BY_SERVICE__ = true;
  }
};
